/**
 * C6 — the kiosk's proof that it reached a booking through a LOOKUP.
 *
 * `POST /kiosk/resolve-booking` is what every kiosk payment surface calls
 * before it renders a price, and it used to answer for any booking id a caller
 * named. Booking ids are ObjectIds — a timestamp and a counter — so "name an
 * id, read back the price, the payment state and the staff member who
 * discounted it" was an enumeration primitive, not a liveness check.
 *
 * The kiosk has no login and no device credential, so the gate cannot be a
 * credential. It is instead proof of REACH: the backend stamps an
 * `X-Kiosk-Lookup-Grant` header on every kiosk entry point that already
 * required knowing something — the booking number, the customer's email, a
 * scanned customer id — or that created the booking at this terminal. This
 * module captures that header and replays it on resolve.
 *
 * ── Why a module-level store and not React state ─────────────────────────
 * Four entry points mint the grant and two screens consume it, several
 * components apart. Prop-drilling a capability token through that tree is how
 * one branch ends up silently ungranted. `sessionStorage` also survives the
 * tab-restore the kiosk explicitly supports (the payment surfaces re-resolve
 * on `visibilitychange`), which in-memory state would not.
 *
 * It is a CAPABILITY, not a secret: it names booking ids the caller already
 * knows and carries an HMAC and an expiry. Losing it costs a re-lookup.
 */

const STORAGE_KEY = 'kiosk_lookup_grant';
export const KIOSK_LOOKUP_GRANT_HEADER = 'X-Kiosk-Lookup-Grant';

// sessionStorage throws in some embedded/private-mode webviews. A kiosk that
// cannot store the grant still works — it simply re-looks-up — so every access
// fails soft rather than taking the screen down.
let memoryFallback = null;

/** Remember the grant a kiosk lookup response carried, if it carried one. */
export function captureKioskLookupGrant(response) {
  const headers = response?.headers;
  const token = typeof headers?.get === 'function'
    ? headers.get(KIOSK_LOOKUP_GRANT_HEADER)
    : (headers?.[KIOSK_LOOKUP_GRANT_HEADER.toLowerCase()] || headers?.[KIOSK_LOOKUP_GRANT_HEADER]);
  if (!token) return null;
  memoryFallback = token;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, token);
  } catch {
    // memoryFallback still holds it for this page life.
  }
  return token;
}

/** The current grant, or null. */
export function getKioskLookupGrant() {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // fall through to the in-memory copy
  }
  return memoryFallback;
}

/** Headers to merge into a resolve-booking request. Empty when ungranted. */
export function kioskLookupGrantHeaders() {
  const token = getKioskLookupGrant();
  return token ? { [KIOSK_LOOKUP_GRANT_HEADER]: token } : {};
}

/** Drop the grant — end of a kiosk session, or a flow reset. */
export function clearKioskLookupGrant() {
  memoryFallback = null;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clean up
  }
}

export default {
  captureKioskLookupGrant,
  getKioskLookupGrant,
  kioskLookupGrantHeaders,
  clearKioskLookupGrant,
  KIOSK_LOOKUP_GRANT_HEADER,
};
